import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save, RotateCcw, Image as ImageIcon, Upload, Plus, X } from "lucide-react";
import { toast } from "sonner";

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

async function pickImage(setter) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Image must be under 8 MB");
            return;
        }
        const dataUrl = await fileToBase64(f);
        setter(dataUrl);
    };
    input.click();
}

export default function SiteContentEditor() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        api.get("/site-content")
            .then((res) => setContent(res.data))
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => setLoading(false));
    }, []);

    const updateHero = (key, value) =>
        setContent((c) => ({ ...c, hero: { ...c.hero, [key]: value } }));

    const updateService = (slug, key, value) =>
        setContent((c) => ({
            ...c,
            services: c.services.map((s) => (s.slug === slug ? { ...s, [key]: value } : s)),
        }));

    const updateIncludes = (slug, raw) => {
        const list = raw.split("\n").map((l) => l.trim()).filter(Boolean);
        updateService(slug, "includes", list);
    };

    const onSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/site-content", content);
            setContent(data);
            toast.success("Site content saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const onReset = async () => {
        if (!window.confirm("Reset all hero and service content to the original defaults? Your uploaded portfolio photos are not affected.")) return;
        setResetting(true);
        try {
            const { data } = await api.post("/site-content/reset");
            setContent(data);
            toast.success("Reset to defaults");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setResetting(false);
        }
    };

    if (loading || !content) {
        return <p className="overline" data-testid="site-content-loading">Loading content…</p>;
    }

    return (
        <div data-testid="site-content-editor" className="space-y-12">
            <div className="flex items-center justify-between gap-4 flex-wrap border-b border-[#E5DED5] pb-6">
                <div>
                    <p className="overline">Edit home page</p>
                    <p className="font-serif text-2xl mt-1">Hero & services — text and images.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        data-testid="site-content-reset"
                        onClick={onReset}
                        disabled={resetting}
                        className="btn-editorial !py-2 !px-4 !text-[0.7rem]"
                    >
                        {resetting ? "Resetting…" : (<><RotateCcw className="w-3.5 h-3.5" /> Reset</>)}
                    </button>
                    <button
                        data-testid="site-content-save"
                        onClick={onSave}
                        disabled={saving}
                        className="btn-editorial primary !py-2 !px-4 !text-[0.7rem]"
                    >
                        {saving ? "Saving…" : (<><Save className="w-3.5 h-3.5" /> Save changes</>)}
                    </button>
                </div>
            </div>

            <section data-testid="hero-editor" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                    <p className="overline mb-3">Hero image</p>
                    <ImagePicker
                        value={content.hero.image}
                        onPick={() => pickImage((v) => updateHero("image", v))}
                        onClear={() => updateHero("image", "")}
                        testId="hero-image-picker"
                    />
                    <div className="mt-5 space-y-4">
                        <Field
                            label="Frame label"
                            value={content.hero.frame_label}
                            onChange={(v) => updateHero("frame_label", v)}
                            testId="hero-frame-label"
                        />
                        <Field
                            label="Frame value"
                            value={content.hero.frame_value}
                            onChange={(v) => updateHero("frame_value", v)}
                            testId="hero-frame-value"
                        />
                    </div>
                </div>
                <div className="lg:col-span-8 space-y-5">
                    <Field
                        label="Eyebrow (small text above headline)"
                        value={content.hero.eyebrow}
                        onChange={(v) => updateHero("eyebrow", v)}
                        testId="hero-eyebrow-input"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field
                            label="Headline · lead"
                            value={content.hero.headline_lead}
                            onChange={(v) => updateHero("headline_lead", v)}
                            testId="hero-headline-lead"
                        />
                        <Field
                            label="Headline · emphasis (italic accent)"
                            value={content.hero.headline_emphasis}
                            onChange={(v) => updateHero("headline_emphasis", v)}
                            testId="hero-headline-emphasis"
                        />
                        <Field
                            label="Headline · trail"
                            value={content.hero.headline_trail}
                            onChange={(v) => updateHero("headline_trail", v)}
                            testId="hero-headline-trail"
                        />
                    </div>
                    <Field
                        label="Subtext"
                        value={content.hero.subtext}
                        onChange={(v) => updateHero("subtext", v)}
                        textarea
                        rows={4}
                        testId="hero-subtext-input"
                    />
                </div>
            </section>

            <section data-testid="services-editor" className="space-y-10">
                <p className="overline">Services & pricing</p>
                {content.services.map((s) => (
                    <ServiceEditor
                        key={s.slug}
                        service={s}
                        onChange={(key, value) => updateService(s.slug, key, value)}
                        onChangeIncludes={(raw) => updateIncludes(s.slug, raw)}
                        onPickImage={() => pickImage((v) => updateService(s.slug, "image", v))}
                        onClearImage={() => updateService(s.slug, "image", "")}
                    />
                ))}
            </section>

            <ContactEditor
                contact={content.contact || {}}
                onChange={(key, value) =>
                    setContent((c) => ({ ...c, contact: { ...(c.contact || {}), [key]: value } }))
                }
                onChangeSocials={(socials) =>
                    setContent((c) => ({ ...c, contact: { ...(c.contact || {}), socials } }))
                }
            />

            <div className="border-t border-[#E5DED5] pt-6 flex justify-end gap-3">
                <button
                    data-testid="site-content-save-bottom"
                    onClick={onSave}
                    disabled={saving}
                    className="btn-editorial primary"
                >
                    {saving ? "Saving…" : (<>Save all changes <Save className="w-4 h-4" /></>)}
                </button>
            </div>
        </div>
    );
}

function ServiceEditor({ service, onChange, onChangeIncludes, onPickImage, onClearImage }) {
    return (
        <div data-testid={`service-editor-${service.slug}`} className="border border-[#E5DED5] bg-[#F2EFE9]/30 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <p className="font-serif text-2xl">
                    {service.title || "Untitled service"}{" "}
                    <span className="overline ml-2 align-middle">{service.slug}</span>
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                    <p className="overline mb-3">Image</p>
                    <ImagePicker
                        value={service.image}
                        onPick={onPickImage}
                        onClear={onClearImage}
                        testId={`service-${service.slug}-image-picker`}
                    />
                </div>
                <div className="lg:col-span-8 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                            label="Eyebrow"
                            value={service.eyebrow}
                            onChange={(v) => onChange("eyebrow", v)}
                            testId={`service-${service.slug}-eyebrow-input`}
                        />
                        <Field
                            label="Title"
                            value={service.title}
                            onChange={(v) => onChange("title", v)}
                            testId={`service-${service.slug}-title-input`}
                        />
                    </div>
                    <Field
                        label="Description"
                        value={service.description}
                        onChange={(v) => onChange("description", v)}
                        textarea
                        rows={3}
                        testId={`service-${service.slug}-description-input`}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                            label="Price (e.g. $275)"
                            value={service.price}
                            onChange={(v) => onChange("price", v)}
                            testId={`service-${service.slug}-price-input`}
                        />
                        <Field
                            label="Unit (e.g. per session)"
                            value={service.unit}
                            onChange={(v) => onChange("unit", v)}
                            testId={`service-${service.slug}-unit-input`}
                        />
                    </div>
                    <Field
                        label="What's included (one item per line)"
                        value={(service.includes || []).join("\n")}
                        onChange={onChangeIncludes}
                        textarea
                        rows={4}
                        testId={`service-${service.slug}-includes-input`}
                    />
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, textarea, rows = 2, testId }) {
    return (
        <div>
            <label className="overline">{label}</label>
            {textarea ? (
                <textarea
                    data-testid={testId}
                    rows={rows}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="input-underline resize-none"
                />
            ) : (
                <input
                    data-testid={testId}
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="input-underline"
                />
            )}
        </div>
    );
}

function ImagePicker({ value, onPick, onClear, testId }) {
    return (
        <div data-testid={testId} className="space-y-3">
            <div className="aspect-[4/3] border border-[#E5DED5] bg-[#F9F8F6] overflow-hidden relative">
                {value ? (
                    <img src={value} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5C5853]">
                        <ImageIcon className="w-8 h-8 mb-3" />
                        <p className="text-sm">No image</p>
                    </div>
                )}
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    data-testid={`${testId}-pick`}
                    onClick={onPick}
                    className="btn-editorial !py-2 !px-3 !text-[0.65rem] flex-1 justify-center"
                >
                    <Upload className="w-3.5 h-3.5" /> {value ? "Replace" : "Upload"}
                </button>
                {value && (
                    <button
                        type="button"
                        data-testid={`${testId}-clear`}
                        onClick={onClear}
                        className="btn-editorial !py-2 !px-3 !text-[0.65rem]"
                    >
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
}


function ContactEditor({ contact, onChange, onChangeSocials }) {
    const socials = contact.socials || [];

    const addSocial = () =>
        onChangeSocials([...socials, { label: "", handle: "", url: "" }]);

    const updateSocial = (idx, key, value) => {
        const next = socials.map((s, i) => (i === idx ? { ...s, [key]: value } : s));
        onChangeSocials(next);
    };

    const removeSocial = (idx) => {
        onChangeSocials(socials.filter((_, i) => i !== idx));
    };

    return (
        <section data-testid="contact-editor" className="space-y-6 border border-[#E5DED5] bg-[#F2EFE9]/30 p-6 md:p-8">
            <div>
                <p className="overline">Contact info</p>
                <p className="font-serif text-2xl mt-1">How people reach you.</p>
                <p className="text-sm text-[#5C5853] mt-2">
                    Shown on the Contact page and in the footer. Leave any field blank to hide it.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                    label="Email"
                    value={contact.email}
                    onChange={(v) => onChange("email", v)}
                    testId="contact-editor-email"
                />
                <Field
                    label="Phone (optional)"
                    value={contact.phone}
                    onChange={(v) => onChange("phone", v)}
                    testId="contact-editor-phone"
                />
                <Field
                    label="City"
                    value={contact.city}
                    onChange={(v) => onChange("city", v)}
                    testId="contact-editor-city"
                />
                <Field
                    label="Region / subtitle"
                    value={contact.region}
                    onChange={(v) => onChange("region", v)}
                    testId="contact-editor-region"
                />
            </div>

            <Field
                label="Studio hours (multi-line)"
                value={contact.studio_hours}
                onChange={(v) => onChange("studio_hours", v)}
                textarea
                rows={3}
                testId="contact-editor-studio-hours"
            />

            <div className="border-t border-[#E5DED5] pt-6">
                <div className="flex items-center justify-between mb-4">
                    <p className="overline">Social handles</p>
                    <button
                        type="button"
                        data-testid="contact-editor-add-social"
                        onClick={addSocial}
                        className="btn-editorial !py-2 !px-3 !text-[0.65rem]"
                    >
                        <Plus className="w-3.5 h-3.5" /> Add social
                    </button>
                </div>
                {socials.length === 0 ? (
                    <p className="text-sm text-[#5C5853]">No socials yet. Click "Add social" to add Instagram, X, TikTok, etc.</p>
                ) : (
                    <div className="space-y-4">
                        {socials.map((s, i) => (
                            <div
                                key={i}
                                data-testid={`contact-editor-social-${i}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border border-[#E5DED5] bg-[#F9F8F6] p-4"
                            >
                                <div className="md:col-span-3">
                                    <Field
                                        label="Label (e.g. Instagram)"
                                        value={s.label}
                                        onChange={(v) => updateSocial(i, "label", v)}
                                        testId={`contact-editor-social-${i}-label`}
                                    />
                                </div>
                                <div className="md:col-span-3">
                                    <Field
                                        label="Handle (e.g. @you)"
                                        value={s.handle}
                                        onChange={(v) => updateSocial(i, "handle", v)}
                                        testId={`contact-editor-social-${i}-handle`}
                                    />
                                </div>
                                <div className="md:col-span-5">
                                    <Field
                                        label="URL"
                                        value={s.url}
                                        onChange={(v) => updateSocial(i, "url", v)}
                                        testId={`contact-editor-social-${i}-url`}
                                    />
                                </div>
                                <div className="md:col-span-1 flex justify-end">
                                    <button
                                        type="button"
                                        data-testid={`contact-editor-social-${i}-remove`}
                                        onClick={() => removeSocial(i)}
                                        className="text-[#7A2E2E] hover:text-[#1A1817] p-2"
                                        aria-label="Remove social"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
